import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectHexes,
  selectPendingBuildings,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {checkIfHexBlockedByEnemyUnit} from "../checkIfHexBlockedByEnemyUnit";
import {calculateFutureCitiesCache} from "../calculateFutureCitiesCache";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {getCityHexKeys} from "../getCityHexKeys";
import {selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {POLICY_OLIGARCHY_GUILD_MAX_COUNT_IN_ONE_CITY} from "../../../configs/policy/oligarchy";

/**
 * Проверяет, можно ли построить такое специальное строение в городе.
 * Стандартное ограничение правил: если строение такого типа уже есть в городе,
 * то еще одно строение такого же типа в городе построить нельзя.
 * За исключением тех случаев, которые отдельно обработаны в данной функции.
 * @param owner
 * @param {Object} city - Объект города
 * @param {string} buildingType - Тип специального строения
 * @returns {boolean}
 */
const checkIfCityCouldHaveMoreBuildingsOfType = (owner, city, buildingType) => {
  const buildings = city[buildingType];
  if (!buildings || buildings.length === 0) {
    return true;
  }

  if (buildingType === SPECIAL_BUILDING_TOKENS.GUILD
    && isPlayerPolicy(owner, POLICIES.OLIGARCHY)
  ) {
    return buildings.length < POLICY_OLIGARCHY_GUILD_MAX_COUNT_IN_ONE_CITY;
  } else {
    return false;
  }
};

/**
 * Вычисляет доступные ячейки для размещения специальных строений.
 * Специальные строения можно размещать на любых обычных кварталах игрока (quarter),
 * включая те, которые находятся в состоянии pendingBuildings (еще не попали в citiesCache).
 *
 * Если в городе уже есть такое же строение, то все кварталы этого города исключаются.
 *
 * @param {Object} state - Redux state
 * @param [precomputedFutureCitiesCache] - уже посчитанный для этого же состояния
 *   calculateFutureCitiesCache, чтобы не считать его заново
 * @returns {Array<string>} Массив ключей ячеек (hexKey), доступных для размещения специального строения
 */
export const calculateAvailableConstructionCellsForSpecialBuilding = (state, precomputedFutureCitiesCache = null) => {
  const pendingBuildings = selectPendingBuildings(state);
  const currentPlayer = selectCurrentPlayer(state);
  const hexes = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const units = selectUnits(state);
  const selectedToken = selectSelectedToken(state);
  const buildingsDamage = selectBuildingsDamage(state);

  const futureCitiesCache = precomputedFutureCitiesCache
    || calculateFutureCitiesCache(hexes, specialBuildings, pendingBuildings);
  const currentPlayerCities = futureCitiesCache[currentPlayer.color] || [];

  let availableCells = [];

  if (selectedToken === SPECIAL_BUILDING_TOKENS.FORTRESS) {
    for (const city of currentPlayerCities) {
      availableCells.push(...getCityHexKeys(city))
    }
  } else {
    // Собираем все ключи обычных кварталов из всех городов игрока.
    // Исключаем города, где уже есть выбранное специальное строение.
    for (const city of currentPlayerCities) {
      // Проверяем, есть ли уже такое специальное строение в этом городе
      if (selectedToken && !checkIfCityCouldHaveMoreBuildingsOfType(currentPlayer, city, selectedToken)) {
        // Пропускаем все кварталы этого города
        continue;
      }

      const quarters = city[COMMON_BUILDING_TOKENS.QUARTER]

      if (quarters && Array.isArray(quarters)) {
        availableCells.push(...quarters);
      }
    }
  }

  return availableCells.filter(hexKey => {
    if (specialBuildings[hexKey]) {
      // Нельзя строить спецстроение на ячейке, которое уже занято другим спецстроением.
      return false;
    }

    // Передаем в проверку базовый квартал, так как специальные строения можно строить только поверх кварталов.
    // Внутри также будет выполнена проверка, не поврежден ли сам квартал.
    return !checkIfHexBlockedByEnemyUnit(hexKey, TOKENS.QUARTER, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);
  });
};